import React from 'react';
import { CheckCircle, Ban, RefreshCw, Coins, Settings, Building2 } from 'lucide-react';
import { Company } from '../../../services/api';
import { CopyButton } from '../../../utils/copyToClipboard';
import Pagination from '../../common/Pagination';

interface CompaniesTableProps {
  companies: Company[];
  onRefreshCompanyData: (companyId: string) => void;
  onAddFunds: (company: Company) => void;
  onUpdateCompanyLimits: (company: Company) => void;
  onManageAllowedCompanies: (company: Company) => void;
  onApproveCompany: (companyId: string) => void;
  onSuspendCompany: (companyId: string) => void;
  onSelectCompany?: (companyId: string) => void;
  selectedCompanyId?: string;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showPagination?: boolean;
}

const CompaniesTable: React.FC<CompaniesTableProps> = ({
  companies,
  onRefreshCompanyData,
  onAddFunds,
  onUpdateCompanyLimits,
  onManageAllowedCompanies,
  onSelectCompany,
  selectedCompanyId,
  onApproveCompany,
  onSuspendCompany,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  showPagination = false
}) => {
  const formatCurrency = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  if (companies.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No companies found.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#182233] rounded-lg shadow">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-700">
            <tr>
              {onSelectCompany && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Select</th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Company</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Wallet</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[#182233] divide-y divide-gray-600">
            {companies.map((company) => (
              <tr key={company.id} className={selectedCompanyId === company.id ? 'bg-blue-900/20' : ''}>
                {onSelectCompany && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => onSelectCompany(company.id)}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        selectedCompanyId === company.id
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-400 hover:border-blue-500'
                      }`}
                    >
                      {selectedCompanyId === company.id && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-white">{company.companyName}</div>
                    <div className="text-sm text-gray-400">{company.companyEmail}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                  <div className="flex items-center space-x-2">
                    <span>{company.companyWallet?.slice(0, 8)}...{company.companyWallet?.slice(-8)}</span>
                    {company.companyWallet && <CopyButton text={company.companyWallet} />}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    company.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                    company.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {company.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                  {formatCurrency(company.valueAmount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onRefreshCompanyData(company.id)}
                      className="text-[#2777EF] hover:text-[#0053E2]"
                      title="Refresh Company Data"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    {company.status === 'ACTIVE' && (
                      <button
                        onClick={() => onAddFunds(company)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Add Funds"
                      >
                        <Coins className="h-4 w-4" />
                      </button>
                    )}
                    {company.status === 'ACTIVE' && (
                      <button
                        onClick={() => onUpdateCompanyLimits(company)}
                        className="text-orange-600 hover:text-orange-900"
                        title="Update Company Limits"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    )}
                    {company.status === 'ACTIVE' && (
                      <button
                        onClick={() => onManageAllowedCompanies(company)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Manage Allowed Companies"
                      >
                        <Building2 className="h-4 w-4" />
                      </button>
                    )}
                    {(company.status === 'PENDING' || company.status === 'SUSPENDED') && (
                      <button
                        onClick={() => onApproveCompany(company.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Approve Company"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                    )}
                    {company.status === 'ACTIVE' && (
                      <button
                        onClick={() => onSuspendCompany(company.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Suspend Company"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {showPagination && onPageChange && onItemsPerPageChange && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
    </div>
  );
};

export default CompaniesTable;
